import React, { useEffect, useMemo, useState } from "react";
import {
  Table,
  LoadingOverlay,
  Input,
  Modal,
  Button,
  Loader,
  Skeleton,
} from "@mantine/core";
import SensorLogs from "./SensorLogs";
import { TbDatabaseOff } from "react-icons/tb";
import { IoMdExpand } from "react-icons/io";
import { LuChevronsUpDown } from "react-icons/lu";
import CustomTable from "./Table";
import { CiViewList } from "react-icons/ci";
import { CgMoreO } from "react-icons/cg";
import { LuMoreHorizontal } from "react-icons/lu";
import { useGetSensorsQuery } from "../hooks/hooks";
import { IoSearchOutline } from "react-icons/io5";
import { formatTimestampToDate } from "../utils/general";
import { useLazyGetSensorQuery } from "../redux/slices/api.slice";
import { useAppSelector } from "../redux/hooks";

import { useParams } from "react-router-dom";
import "./styles/common.css";

const SensorsTable = () => {
  const { id } = useParams();
  const { sensors } = useAppSelector((state) => state.groups);
  const [
    getSensorDetail,
    { data, isFetching: isFetchingSeneorDetail, isError: isErrorSeneorDetail },
  ] = useLazyGetSensorQuery();
  const { isLoading, isError } = useGetSensorsQuery(id);
  const [filteredData, setFilteredData] = useState([]);
  const [toggleSorting, setToggleSorting] = useState(false);
  const [search, setSearch] = useState("");
  const [sensorLogs, setSensorLogs] = useState({ sensorID: "", open: false });
  const [sensorDetail, setSensorDetail] = useState(null);
  const updatedTimestamp = (time: string) => {
    return new Date(time).getTime();
  };

  const floorSensors = useMemo(() => {
    return sensors?.[id] || [];
  }, [sensors]);

  useEffect(() => {
    if (floorSensors.length > 0) {
      const filterByTimestamp = [...floorSensors].sort((a, b) =>
        toggleSorting
          ? updatedTimestamp(a.timestamp) - updatedTimestamp(b.timestamp)
          : updatedTimestamp(b.timestamp) - updatedTimestamp(a.timestamp)
      );
      if (search === "") {
        setFilteredData(filterByTimestamp);
      } else {
        const updated = filterByTimestamp.filter((item) => {
          const isAssigned =
            item.assigned && "assigned".includes(search.toLowerCase());
          const isUnassigned =
            !item.assigned && "unassigned".includes(search.toLowerCase());
          return (
            item.id?.toLowerCase().includes(search.toLowerCase()) ||
            item.name?.toLowerCase().includes(search.toLowerCase()) ||
            isAssigned ||
            isUnassigned ||
            formatTimestampToDate(item.timestamp)
              .toLowerCase()
              .includes(search.toLowerCase())
          );
        });
        setFilteredData(updated);
      }
    }
  }, [search, floorSensors]);

  const handleTimestampSorting = () => {
    const updated = [...filteredData].sort((a, b) =>
      !toggleSorting
        ? updatedTimestamp(a.timestamp) - updatedTimestamp(b.timestamp)
        : updatedTimestamp(b.timestamp) - updatedTimestamp(a.timestamp)
    );
    setFilteredData(updated);
    setToggleSorting(!toggleSorting);
  };
  const [detailsModal, setDetailsModal] = useState({
    id: undefined,
    open: false,
  });
  const isAssignedSensorExist = useMemo(() => {
    return filteredData.some((el) => el.assigned);
  }, [filteredData]);

  useEffect(() => {
    if (data) {
      const updated = { ...sensorDetail, [data.id]: data };
      setSensorDetail(updated);
    }
  }, [data]);

  const handleSensorDetail = async (sensorID) => {
    const result = await getSensorDetail(sensorID, true);
  };

  const rows = filteredData.map((item) => {
    return (
      <Table.Tr key={item.id}>
        <Table.Td>{item.id}</Table.Td>

        <Table.Td>{item.name}</Table.Td>

        <Table.Td>{item.assigned ? "Assigned" : "Unassigned"}</Table.Td>
        <Table.Td>{formatTimestampToDate(item.timestamp)}</Table.Td>
        <Table.Td className="flex items-center gap-4 justify-center">
          {item.assigned && (
            <div className="justify-center items-center flex h-full">
              <Button
                size="sm"
                variant="outline"
                radius="md"
                leftSection={<CiViewList className="text-2xl" />}
                onClick={() => {
                  handleSensorDetail(item.id);
                  setDetailsModal({ id: item.id, open: true });
                }}
              >
                Detail
              </Button>
            </div>
          )}
          <Button
            size="sm"
            variant="outline"
            radius="md"
            leftSection={<CiViewList className="text-2xl" />}
            onClick={() => {
              setSensorLogs({ sensorID: item.id, open: true });
            }}
          >
            Logs
          </Button>
        </Table.Td>
      </Table.Tr>
    );
  });

  const columnsLabels = ["SensorID", "Name", "Status", "Last Seen", ""];
  const columns = columnsLabels.map((column, index) => {
    return (
      <Table.Th
        key={index}
        className={
          index === 0
            ? "rounded-ss-[10px]"
            : index === columnsLabels.length - 1 && "rounded-se-[10px]"
        }
      >
        {column === "Last Seen" ? (
          <div className="flex gap-4 items-center">
            <span>{column}</span>
            <LuChevronsUpDown
              onClick={handleTimestampSorting}
              className="text-[16px] cursor-pointer"
            />
          </div>
        ) : (
          column
        )}
      </Table.Th>
    );
  });

  return (
    <div className="w-full">
      <div className="flex justify-between items-center mb-10   ">
        <p className="text-lg text-grayText3 ">
          List of all sensors associated with this floor
        </p>
        <Input
          placeholder="Search"
          className="w-fit"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          leftSection={<IoSearchOutline />}
        />
      </div>

      <div className="relative w-full mb-10  border border-grayText4 border-solid rounded-[10px] ">
        <CustomTable
          rows={rows}
          columns={columns}
          isLoading={isLoading}
          isError={isError}
        />
      </div>

      {sensorLogs.open && (
        <SensorLogs
          open={sensorLogs.open}
          handleClose={() => {
            setSensorLogs((prev) => ({ ...prev, open: false }));
          }}
          sensorID={sensorLogs.sensorID}
        />
      )}
      <Modal
        opened={detailsModal.open}
        onClose={() => setDetailsModal({ ...detailsModal, open: false })}
        size={"lg"}
        centered
        withCloseButton={false}
      >
        {isFetchingSeneorDetail ? (
          <div className="w-full h-full flex justify-center items-center">
            <div className="w-full h-full">
              <Skeleton height={28} width={"35%"} radius="md" mt="md" mb="lg" />
              <div className="flex flex-col gap-2 ">
                <Skeleton height={25} radius="md" />
                <Skeleton height={25} radius="md" />
                <Skeleton height={25} radius="md" />
                <Skeleton height={25} radius="md" />
                <Skeleton height={25} radius="md" />
              </div>
              <div className="flex justify-end mt-5">
                <Skeleton height={45} width={"25%"} radius="md" />
              </div>
            </div>
          </div>
        ) : isErrorSeneorDetail ? (
          <div className="w-full h-[270px] flex justify-center items-center">
            Error fetching sensor Data
          </div>
        ) : (
          <div className="flex justify-center h-full  flex-col">
            <h4 className="text-2xl py-4">Sensor Details</h4>
            <SensorDetailList detail={sensorDetail?.[detailsModal.id]} />
            <div className="flex justify-end mt-4">
              <Button
                variant="outline"
                size="md"
                color="#4D5F7A"
                onClick={() =>
                  setDetailsModal({ ...detailsModal, open: false })
                }
              >
                Close
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default SensorsTable;

const SensorDetailList = ({ detail }) => {
  return (
    <ul className="list-none p-0">
      <SensorDetailItem label="ID" value={detail?.id} />
      <SensorDetailItem label="Name" value={detail?.name} />
      <SensorDetailItem label="Assigned Site" value={detail?.locationName} />
      <SensorDetailItem label="Assigned Floor" value={detail?.floorName} />
      <SensorDetailItem label="Assigned Desk" value={detail?.deskAlias} />
    </ul>
  );
};

const SensorDetailItem = ({ label, value }) => {
  if (!value) return null;
  return (
    <li className="flex gap-2 py-1">
      <span className="font-medium">{label}:</span>
      <span>{value}</span>
    </li>
  );
};
